import { useEffect, useState } from "react";
import PageHeader from "../../components/pageHeader";
import AdminAttendanceTabs from "./adminAttendanceTabs";
import QRCode from "react-qr-code";
import { API_BASE } from "../../config/apiConfig";
import { useToast } from "../../hooks/useToast";
import LoadingOverlay from "../../components/loadingOverlay";
import { toLocalInputValue } from "../../utils/date";
import MainContentLayout from "../../components/mainContentLayout";

export default function AdminAttendance() {
  const [loading, setLoading] = useState(true);
  const [activeEvent, setActiveEvent] = useState(null);

  const [startAt, setStartAt] = useState(() => toLocalInputValue(new Date()));
  const [ttl, setTtl] = useState(60);
  const [isPH, setIsPH] = useState(false);
  const [holidayName, setHolidayName] = useState(null);

  const [nowMs, setNowMs] = useState(Date.now());
  const { showToast, ToastContainer } = useToast();

  const token = sessionStorage.getItem("access_token");

  /* ---------------------------------------------
   * Helper: convert datetime-local → YYYY-MM-DD
   * --------------------------------------------- */
  function toYMD(dateStr) {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-CA"); // YYYY-MM-DD
  }

  /* ---------------------------------------------
   * Fetch Active Event on Load
   * --------------------------------------------- */
  useEffect(() => {
    fetchActive();
  }, []);

  async function fetchActive() {
    try {
      const res = await fetch(`${API_BASE}/api/attendance/active`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();

      if (data.success && data.active) {
        setActiveEvent(data.active);
      } else {
        setActiveEvent(null);
      }
    } catch (err) {
      console.error(err);
      showToast("Failed to load attendance", "error");
    }
    setLoading(false);
  }

  /* ---------------------------------------------
   * Timer
   * --------------------------------------------- */
  useEffect(() => {
    if (!activeEvent) return;
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [activeEvent]);

  const remainingSec = (() => {
    if (!activeEvent) return 0;
    const expMs = new Date(activeEvent.expiresAt).getTime();
    return Math.max(0, Math.floor((expMs - nowMs) / 1000));
  })();

  const isExpired = activeEvent && remainingSec <= 0;

  useEffect(() => {
    if (activeEvent && isExpired) {
      setActiveEvent(null);  // clear expired event
    }
  }, [isExpired, activeEvent]);

  /* ---------------------------------------------
   * Holiday Check API
   * --------------------------------------------- */
  async function checkHoliday(dateStr) {
    try {
      const res = await fetch(`${API_BASE}/api/holidays/check`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ date: dateStr }),
      });

      const data = await res.json();

      if (data.success) {
        setIsPH(data.isPH);
        setHolidayName(data.holidayName);
      } else {
        setIsPH(false);
        setHolidayName(null);
      }
    } catch (err) {
      setIsPH(false);
      setHolidayName(null);
    }
  }

  // Trigger holiday check when date changes
  useEffect(() => {
    const ymd = toYMD(startAt);
    checkHoliday(ymd);
  }, [startAt]);

  /* ---------------------------------------------
   * Create New QR Code
   * --------------------------------------------- */
  async function handleCreate() {
    setLoading(true);

    try {
      const localMs = new Date(startAt).getTime();

      const res = await fetch(`${API_BASE}/api/attendance/create`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          baseUrl: window.location.origin,
          ttlMinutes: ttl,
          startAt: localMs,
          isPH,
          holidayName,
        }),
      });

      const data = await res.json();

      if (!data.success) {
        showToast(data.message || "Create failed", "warning");
        return;
      }

      // New active event updated immediately
      setActiveEvent({
        id: data.eventId,
        startAt,
        expiresAt: data.expiresAt,
        isPH,
        holidayName,
        token: data.checkinUrl.split("tk=")[1],
      });

      showToast("QR Code created", "success");
    } catch (err) {
      console.error(err);
      showToast("Error creating QR", "error");
    }
    setLoading(false);
  }

  return (
    <MainContentLayout>
      <PageHeader title="Attendance" backTo="/dashboard" />
      <AdminAttendanceTabs />

      <div className="p-5 mt-4 space-y-6 max-w-2xl mx-auto w-full">

        {/* =========================================================
            ACTIVE QR CODE DISPLAY
        ========================================================== */}
        {activeEvent && !isExpired && (
          <div className="bg-blue-50 rounded-xl p-6 flex flex-col items-center justify-center shadow">
            <div className="bg-white p-4 rounded-lg shadow-md">
              <QRCode
                value={`${window.location.origin}/attend?event=${activeEvent.id}&tk=${activeEvent.token}`}
                size={220}
              />
            </div>
            <p className="mt-4 text-sm text-gray-600">
              Expires in:{" "}
              <span className="font-semibold">
                {String(Math.floor(remainingSec / 60)).padStart(2, "0")}:
                {String(remainingSec % 60).padStart(2, "0")}
              </span>
            </p>
          </div>
        )}

        {/* =========================================================
            CREATE NEW QR CODE FORM (ONLY IF NO ACTIVE EVENT)
        ========================================================== */}
        {!activeEvent && (
          <div className="bg-white rounded-xl shadow p-4 grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Start Date & Time
              </label>
              <input
                type="datetime-local"
                className="w-full border rounded px-3 py-2"
                value={startAt}
                onChange={(e) => setStartAt(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Expires In (minutes)
              </label>
              <select
                className="w-full border rounded px-3 py-2"
                value={ttl}
                onChange={(e) => setTtl(Number(e.target.value))}
              >
                {[5, 10, 15, 20, 30, 45, 60, 90, 120].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                checked={isPH}
                onChange={(e) => setIsPH(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm">
                Public Holiday{" "}
                {holidayName && <span>({holidayName})</span>}
              </span>
            </div>

            <button
              onClick={handleCreate}
              disabled={loading}
              className="w-full bg-blue-600 text-white rounded-lg py-2 hover:bg-blue-700"
            >
              {loading ? "Creating..." : "Create QR Code"}
            </button>
          </div>
        )}
      </div>

      <LoadingOverlay show={loading} text="Loading..." />
      <ToastContainer />
    </MainContentLayout>
  );
}
